import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { useAuth } from "../hooks/useAuth";
import { LineField } from "../components/LineField";
import { PaginationBar, usePagination } from "../components/Pagination";
import {
  fetchPendingOrgs,
  approveInstitution,
  approveOrganisation,
  rejectInstitution,
  rejectOrganisation,
} from "../api/admin";
import { createDownloadUrl } from "../api/storage";
import { C, font } from "../styles/tokens";
import { InlineIcon } from "../components/InlineIcon";

/**
 * AdminPendingQueue - FIFO queue of organisation applications awaiting review.
 *
 * Two-column layout when an application is selected: list on the left, detail
 * on the right. Mobile / narrow screens collapse to a single column.
 *
 * Selecting an application loads its full details. From there the admin can:
 *   - View the uploaded registration document (presigned URL)
 *   - Approve with an optional note
 *   - Reject with a required reason
 */
export function AdminPendingQueue() {
  const { localUser } = useAuth();
  const navigate = useNavigate();
  const isAdmin = localUser?.type === "PLATFORM_ADMIN";

  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Decision state
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Document viewer state
  const [loadingDoc, setLoadingDoc] = useState(false);

  const pager = usePagination(items, 10);

  async function reload() {
    setLoading(true);
    try {
      const data = await fetchPendingOrgs();
      setItems(data.items);
      // If the currently-selected item is gone (just approved/rejected), pick the next one
      if (selected && !data.items.find((i) => i.item.id === selected.item.id)) {
        setSelected(data.items[0] ?? null);
        setMode(null);
        setReason("");
      } else if (!selected) {
        setSelected(data.items[0] ?? null);
      }
    } catch (err) {
      setError(err.message ?? "Failed to load pending queue");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isAdmin) reload();
    else setLoading(false);
  }, [isAdmin]);

  async function viewDocument() {
    if (!selected?.item?.registrationDocumentS3Key) return;
    setLoadingDoc(true);
    try {
      const result = await createDownloadUrl(
        selected.item.registrationDocumentS3Key,
      );
      window.open(result.downloadUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err.message ?? "Failed to load document");
    } finally {
      setLoadingDoc(false);
    }
  }

  async function handleDecide() {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      const kind = selected.kind;
      const id = selected.item.id;
      if (mode === "approve") {
        if (kind === "INSTITUTION")
          await approveInstitution(id, reason.trim() || undefined);
        else await approveOrganisation(id, reason.trim() || undefined);
      } else {
        if (!reason.trim()) {
          setError("A rejection reason is required.");
          setSubmitting(false);
          return;
        }
        if (kind === "INSTITUTION") await rejectInstitution(id, reason.trim());
        else await rejectOrganisation(id, reason.trim());
      }
      setMode(null);
      setReason("");
      await reload();
    } catch (err) {
      setError(err.message ?? "Failed to submit decision");
    } finally {
      setSubmitting(false);
    }
  }

  if (!isAdmin) {
    return (
      <AppShell>
        <div style={cardStyle}>This page is for platform administrators.</div>
      </AppShell>
    );
  }

  if (loading) {
    return (
      <AppShell>
        <div style={{ color: C.textMed }}>Loading queue...</div>
      </AppShell>
    );
  }

  return (
    <AppShell maxWidth={1200}>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <button
          onClick={() => navigate("/admin")}
          style={{
            background: "transparent",
            border: 0,
            color: C.blue,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            padding: "4px 8px",
            fontFamily: font,
          }}
        >
          Back to admin
        </button>
      </div>

      <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 16px" }}>
        Pending applications ({items.length})
      </h1>

      {items.length === 0 ? (
        <div
          style={{ ...cardStyle, textAlign: "center", padding: "40px 24px" }}
        >
          <div style={{ fontSize: 14, color: C.textMed }}>
            No applications waiting for review. Nice work.
          </div>
        </div>
      ) : (
        <div
          data-responsive-grid="2"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(240px, 320px) 1fr",
            gap: 16,
          }}
        >
          {/* Left column: list */}
          <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
            {pager.pageItems.map((entry) => (
              <QueueRow
                key={entry.item.id}
                entry={entry}
                selected={selected?.item.id === entry.item.id}
                onClick={() => {
                  setSelected(entry);
                  setMode(null);
                  setReason("");
                  setError(null);
                }}
              />
            ))}
            <PaginationBar pager={pager} itemLabel="applications" />
          </div>

          {/* Right column: detail */}
          {selected && (
            <div>
              <DetailPanel
                entry={selected}
                onViewDoc={viewDocument}
                loadingDoc={loadingDoc}
              />

              {error && (
                <div
                  style={{
                    ...cardStyle,
                    color: C.red,
                    marginTop: 16,
                    fontSize: 13,
                  }}
                >
                  {error}
                </div>
              )}

              <div style={{ ...cardStyle, marginTop: 16 }}>
                {!mode && (
                  <>
                    <h2
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        margin: "0 0 12px",
                      }}
                    >
                      Your decision
                    </h2>
                    <div style={{ display: "flex", gap: 10 }}>
                      <button
                        onClick={() => setMode("reject")}
                        style={rejectButton}
                      >
                        Reject application
                      </button>
                      <button
                        onClick={() => setMode("approve")}
                        style={approveButton}
                      >
                        Approve application
                      </button>
                    </div>
                  </>
                )}

                {mode === "approve" && (
                  <DecisionForm
                    title="Approve application"
                    subtitle={`Approving will let ${selected.item.name} start verifying credentials immediately. They become visible in the verifier dropdown for Professionals.`}
                    label="Approval note (optional)"
                    placeholder="Any internal note about this approval..."
                    value={reason}
                    onChange={setReason}
                    onCancel={() => {
                      setMode(null);
                      setReason("");
                    }}
                    onConfirm={handleDecide}
                    confirmLabel="Confirm approval"
                    confirmColor={C.green}
                    submitting={submitting}
                  />
                )}

                {mode === "reject" && (
                  <DecisionForm
                    title="Reject application"
                    subtitle={`The applicant sees this reason. Be specific so they can address the concern if they reapply.`}
                    label="Reason (required)"
                    placeholder="e.g. The registration document doesn't match the institution name."
                    value={reason}
                    onChange={setReason}
                    onCancel={() => {
                      setMode(null);
                      setReason("");
                    }}
                    onConfirm={handleDecide}
                    confirmLabel="Confirm rejection"
                    confirmColor={C.red}
                    submitting={submitting}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}

function QueueRow({ entry, selected, onClick }) {
  const { kind, item } = entry;
  return (
    <div
      onClick={onClick}
      style={{
        padding: 14,
        borderLeft: selected ? `3px solid ${C.blue}` : `3px solid transparent`,
        borderBottom: `1px solid ${C.divider}`,
        background: selected ? C.blueLight : C.bg,
        cursor: "pointer",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: C.textLight,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          {kind === "INSTITUTION" ? "Institution" : "Organisation"}
        </span>
        {item.reviewReason && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#92400E",
              background: "#FEF3C7",
              border: "1px solid #F59E0B",
              borderRadius: 4,
              padding: "1px 5px",
            }}
          >
            <InlineIcon name="warning" size={10} /> {item.reviewReason}
          </span>
        )}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: C.textDark }}>
        {item.name}
      </div>
      <div style={{ fontSize: 12, color: C.textMed, marginTop: 2 }}>
        {placeOf(item) || "No address given"}
      </div>
      <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>
        Submitted {formatRelative(item.createdAt)}
      </div>
    </div>
  );
}

function DetailPanel({ entry, onViewDoc, loadingDoc }) {
  const { kind, item } = entry;
  const admin = item.officers?.[0];
  const adminEmail = admin?.user?.email;
  const adminPhone = admin?.user?.phone;
  const panVerification = item.panVerification;

  return (
    <div style={cardStyle}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: C.textLight,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          marginBottom: 4,
        }}
      >
        {kind === "INSTITUTION" ? "Academic institution" : "Organisation"}
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 4px" }}>
        {item.name}
      </h2>
      <div style={{ fontSize: 13, color: C.textMed }}>
        Reg no: {item.registrationNumber}
        {item.websiteUrl && (
          <>
            {" "}
            -{" "}
            <a
              href={item.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: C.blue }}
            >
              website
            </a>
          </>
        )}
      </div>

      {panVerification && (
        <>
          <SectionHeading>PAN Identity Verification</SectionHeading>
          <Field label="PAN Number" value={panVerification.pan} />
          <Field
            label="PAN Category"
            value={`${panVerification.category} - ${panVerification.categoryDescription || "N/A"}`}
          />
          <Field label="Name on PAN" value={panVerification.nameOnPan} />
          {item.reviewReason && (
            <div
              style={{
                marginTop: 10,
                padding: "8px 12px",
                background: "#FEF3C7",
                border: "1px solid #F59E0B",
                borderRadius: 4,
                color: "#92400E",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <InlineIcon name="warning" size={12} /> Review Flag:{" "}
              {item.reviewReason} (Requires manual admin verification before
              approval)
            </div>
          )}
        </>
      )}

      <SectionHeading>Contact</SectionHeading>
      <Field label="Email" value={item.contactEmail} />
      <Field label="Phone" value={item.contactPhone} />

      <SectionHeading>Address</SectionHeading>
      <div style={{ fontSize: 13, color: C.textDark, lineHeight: 1.5 }}>
        {addressLines(item).length > 0
          ? addressLines(item).map((line) => <div key={line}>{line}</div>)
          : "Not given at sign-up (a PAN record has no address)."}
      </div>

      <SectionHeading>Admin officer</SectionHeading>
      {admin ? (
        <>
          <Field label="Name" value={admin.fullName} />
          <Field label="Designation" value={admin.designation} />
          <Field label="Employee ID" value={admin.employeeId} />
          {adminEmail && <Field label="Email" value={adminEmail} />}
          {adminPhone && <Field label="Phone" value={adminPhone} />}
        </>
      ) : (
        <div style={{ fontSize: 13, color: C.textLight }}>
          No officer information available.
        </div>
      )}

      <SectionHeading>Registration document</SectionHeading>
      <button
        onClick={onViewDoc}
        disabled={loadingDoc}
        style={{
          padding: "8px 16px",
          fontSize: 13,
          fontWeight: 600,
          color: C.blue,
          background: "transparent",
          border: `1px solid ${C.blue}`,
          borderRadius: 4,
          cursor: loadingDoc ? "default" : "pointer",
          fontFamily: font,
        }}
      >
        {loadingDoc ? "Loading..." : "View document"}
      </button>
    </div>
  );
}

function DecisionForm({
  title,
  subtitle,
  label,
  placeholder,
  value,
  onChange,
  onCancel,
  onConfirm,
  confirmLabel,
  confirmColor,
  submitting,
}) {
  return (
    <>
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 4px" }}>
        {title}
      </h2>
      <p
        style={{
          fontSize: 13,
          color: C.textMed,
          lineHeight: 1.5,
          margin: "0 0 12px",
        }}
      >
        {subtitle}
      </p>
      <LineField
        label={label}
        as="textarea"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <button onClick={onCancel} style={secondaryButton}>
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={submitting}
          style={{
            flex: 2,
            padding: "12px",
            fontSize: 14,
            fontWeight: 600,
            color: "white",
            background: submitting ? C.textLight : confirmColor,
            border: 0,
            borderRadius: 4,
            cursor: submitting ? "default" : "pointer",
            fontFamily: font,
          }}
        >
          {submitting ? "Submitting..." : confirmLabel}
        </button>
      </div>
    </>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 4 }}>
      <div
        style={{
          fontSize: 11,
          color: C.textLight,
          textTransform: "uppercase",
          letterSpacing: 0.3,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, color: C.textDark }}>{value}</div>
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: C.textMed,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        margin: "16px 0 8px",
      }}
    >
      {children}
    </div>
  );
}

/** "Chennai, Tamil Nadu", or "" when the sign-up gave no place. */
function placeOf(item) {
  return [item.city, item.state].filter(Boolean).join(", ");
}

/** The address as given at sign-up, line by line; none for a PAN sign-up. */
function addressLines(item) {
  return [
    item.addressLine1,
    item.addressLine2,
    [placeOf(item), item.pincode].filter(Boolean).join(" - "),
  ].filter(Boolean);
}

function formatRelative(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });
}

const cardStyle = {
  background: C.bg,
  border: `1px solid ${C.divider}`,
  borderRadius: 8,
  padding: 20,
};

const approveButton = {
  flex: 1,
  padding: "12px 16px",
  fontSize: 14,
  fontWeight: 600,
  color: "white",
  background: C.green,
  border: 0,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: font,
};
const rejectButton = {
  flex: 1,
  padding: "12px 16px",
  fontSize: 14,
  fontWeight: 600,
  color: "white",
  background: C.red,
  border: 0,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: font,
};
const secondaryButton = {
  flex: 1,
  padding: "12px",
  fontSize: 14,
  fontWeight: 600,
  color: C.textDark,
  background: "transparent",
  border: `1px solid ${C.dividerStrong}`,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: font,
};
